import {announce} from '@github-ui/aria-live'
import {parseFileAnchor} from '@github-ui/blob-anchor'
import {useCopilotSettings} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {useSplitCodeownersErrorsContext} from '@github-ui/code-view-shared/contexts/SplitCodeownersErrorsContext'
import {useUrlCreator} from '@github-ui/code-view-shared/hooks/use-url-creator'
import {copyText} from '@github-ui/copy'
import {hasHiddenUnicodeCharacters} from '@github-ui/hidden-unicode-banner/utils'
// eslint-disable-next-line no-restricted-imports -- This adapter renders only on the blob route, never in an overview partial.
import {useLocation} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {parseDirectives} from '@github-ui/syntax-highlighted-lines/ast-utils'
import type {SourceRange, SourceSymbol} from '@github-ui/taillights-worker/document'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {useSearchParams} from '@github-ui/use-navigate'
import {
  type ComponentProps,
  use,
  useCallback,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {flushSync} from 'react-dom'

import {useDeferredMetadata} from '../../../../contexts/DeferredMetadataContext'
import {useCurrentStyledBlob} from '../../../../hooks/CurrentStyledBlob'
import {useHighlightedLineMenuOptions} from '../../../../hooks/use-highlighted-line-menu-options'
import {useScrollLineIntoView} from '../../../../hooks/use-scroll-line-into-view'
import {useSharedLineObserver} from '../../../../hooks/use-shared-line-observer'
import {useStickyHeaderHeight} from '../../../../hooks/use-sticky-header-height'
import {textAreaId} from '../../../../utilities/lines'
import {CodeBlobTaillights} from '../../../taillights/CodeBlobTaillights'
import {TaillightsSelectionContext} from '../../../taillights/source-context'
import {SourceModelProvider, useSourceModelContext} from '../../../taillights/source-model'
import {anchorFromSelection, lastSelectedLine, lineRange, selectedText} from '../../../taillights/source-navigation'
import type {TaillightsGutterAction} from '../../../taillights/TaillightsGutterRow'
import {HiddenUnicodeAlert} from '../../Banners/HiddenUnicode'
import {CopilotButton, CopilotButtonContainer, type CopilotButtonHandle} from '../CopilotButton'
import type {CodeBlob} from './CodeBlob'
import {HighlightedLinesProvider} from './HighlightedLineProvider'
import type {CodeLineData} from './hooks/use-code-lines'

type Props = ComponentProps<typeof CodeBlob>

/**
 * Connects the feature-flagged Taillights viewer to Code View's existing CodeBlob contract.
 *
 * BlobContent selects this adapter after eligibility checks. It requires Code View's
 * route contexts and an upstream CodeBlobTaillightsProvider, which supplies the source
 * and shared worker; this adapter does not create another worker or select a renderer.
 *
 * Owns the source interaction model and print lifecycle. BlobSource connects original
 * source coordinates to navigation and selection handles, highlights, sticky headers,
 * line actions, and Copilot selection controls. Printing synchronously materializes
 * plain-text chunks without relying on background graphics or bitmap decoding;
 * normal chunking and bitmap/CSS rendering remain the viewer's concern.
 *
 * The source viewer retains native caret, text selection, and browser Find behavior.
 */
export function TaillightsBlobAdapter({onLineStickOrUnstick, ...props}: Props) {
  const [printing, setPrinting] = useState(false)
  const {hash} = useLocation()
  const {codeFoldingOption} = useCodeViewOptions()
  const stickyHeight = useStickyHeaderHeight()

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/dom-no-flush-sync -- beforeprint must commit readable text before the browser captures its synchronous print snapshot.
    const before = () => flushSync(() => setPrinting(true))
    const after = () => setPrinting(false)
    window.addEventListener('beforeprint', before)
    window.addEventListener('afterprint', after)
    return () => {
      window.removeEventListener('beforeprint', before)
      window.removeEventListener('afterprint', after)
    }
  }, [])

  const removeStickyLine = useCallback(
    (lineNumber: number) => onLineStickOrUnstick({lineNumber}, true),
    [onLineStickOrUnstick],
  )

  return (
    <SourceModelProvider
      showLineActions
      foldingAvailable={codeFoldingOption.enabled}
      onStickyLineRemoval={removeStickyLine}
      onSectionCollapse={removeStickyLine}
      materializeAllLines={printing}
      routerHash={hash}
      hashNavigationOffset={stickyHeight + 8}
    >
      <CopilotButtonContainer>
        <BlobSource {...props} onLineStickOrUnstick={onLineStickOrUnstick} />
      </CopilotButtonContainer>
    </SourceModelProvider>
  )
}

function BlobSource({
  blobLinesHandle,
  onCodeNavTokenSelected,
  searchResults,
  focusedSearchResult,
  codeLineToSectionMap,
  onLineStickOrUnstick,
}: Props) {
  const model = useSourceModelContext()
  const {navigation, gutterSelection} = model
  const {lines, sections, symbolsRef, navigate} = navigation
  const selectionRef = use(TaillightsSelectionContext)

  useImperativeHandle(
    selectionRef,
    () => () => {
      const selection = symbolsRef.current?.getSelection()
      return selection ? {startLine: selection.start.line + 1, endLine: lastSelectedLine(selection) + 1} : null
    },
    [symbolsRef],
  )

  const {range, singleLine} = gutterSelection
  const {copilotAccessAllowed} = useCopilotSettings()
  const copilotRef = useRef<CopilotButtonHandle>(null)
  const anchorRange = navigation.anchorRange
  const copilotLine = anchorRange ? lastSelectedLine(anchorRange) + 1 : null
  const highlightedLines = useMemo(
    () => (anchorRange ? parseFileAnchor(anchorFromSelection(anchorRange, lines)).blobRange : undefined),
    [anchorRange, lines],
  )
  const nativeRef = useCallback((element: HTMLTextAreaElement) => {
    element.id = textAreaId
    return () => element.removeAttribute('id')
  }, [])

  useEffect(() => {
    const anchor =
      copilotLine === null
        ? null
        : (navigation.viewportRef.current?.querySelector<HTMLElement>(`a[data-gutter-line="${copilotLine}"]`) ?? null)
    copilotRef.current?.setAnchor(anchor)
  }, [copilotLine, navigation.viewportRef, navigation.view])

  const {isBrowser} = useRuntimeEnvironment()
  const {codeFoldingOption} = useCodeViewOptions()
  const clearFolds = useEffectEvent(() => navigation.changeFolds(new Set(), {preserveScroll: true}))
  useEffect(() => {
    if (!codeFoldingOption.enabled) clearFolds()
  }, [codeFoldingOption.enabled])
  const {createPermalink, getUrl} = useUrlCreator()
  const {githubDevUrl} = useReposAppPayload()
  const {newIssuePath, newDiscussionPath} = useDeferredMetadata()
  const [params] = useSearchParams()
  const stickyHeight = useStickyHeaderHeight()
  const observer = useSharedLineObserver(true, stickyHeight, onLineStickOrUnstick)
  const {stylingDirectives} = useCurrentStyledBlob()
  const lineMetadata = useMemo(() => {
    const data = new Map<number, CodeLineData>()
    for (const section of sections) {
      if (!navigation.collapsed.has(section.startLine)) {
        data.set(section.startLine, {
          lineNumber: section.startLine,
          rawText: lines[section.startLine - 1],
          stylingDirectivesLine: parseDirectives(stylingDirectives?.[section.startLine - 1] ?? []),
          ownedSection: section,
          isStartLine: true,
        })
        if (!data.has(section.endLine)) data.set(section.endLine, {lineNumber: section.endLine, isEndLine: true})
      }
    }
    return data
  }, [sections, navigation.collapsed, lines, stylingDirectives])
  const onLineMount = useCallback(
    (line: number, element: HTMLAnchorElement) => {
      if (line === copilotLine) copilotRef.current?.setAnchor(element)

      const data = lineMetadata.get(line)

      if (observer && data)
        observer.observe(element, data, Math.max(0, (codeLineToSectionMap?.get(line)?.length ?? 1) - 1))

      return () => {
        if (observer && data) observer.unobserve(element)
        if (line === copilotLine) copilotRef.current?.setAnchor(null)
      }
    },
    [lineMetadata, observer, codeLineToSectionMap, copilotLine],
  )

  const errors = useSplitCodeownersErrorsContext()
  const codeownersErrors = useMemo(
    () =>
      Object.fromEntries(
        errors.map(error => [
          error.line,
          `${error.kind}: ${error.source}${error.suggestion ? ` ${error.suggestion}` : ''}`,
        ]),
      ),
    [errors],
  )
  const highlights = useMemo<SourceRange[]>(() => searchResults.map(({ident}) => ident), [searchResults])
  const focused = focusedSearchResult === undefined ? null : (highlights[focusedSearchResult] ?? null)
  const navigateToLine = useCallback(
    (lineNumber: number, column?: number) => {
      if (!Number.isInteger(lineNumber) || lineNumber < 1 || lineNumber > lines.length) {
        reportError(new RangeError('Cannot navigate outside the source document'))
        return
      }

      const point = {
        line: lineNumber - 1,
        column: Math.max(0, Math.min(column ?? 0, lines[lineNumber - 1]?.length ?? 0)),
      }
      navigate(
        {start: point, end: point},
        false,
        stickyHeight + 8,
        column === undefined ? undefined : `#L${lineNumber}`,
      )
    },
    [lines, navigate, stickyHeight],
  )
  useScrollLineIntoView(({line, column}) => navigateToLine(line, column))
  useImperativeHandle(
    blobLinesHandle,
    () => ({
      scrollToTop: () => navigateToLine(1),
      scrollToLine: (displayLine, column) =>
        navigateToLine((navigation.visible[displayLine] ?? displayLine) + 1, column),
    }),
    [navigateToLine, navigation.visible],
  )
  const reportSymbol = useCallback(
    (symbol: SourceSymbol) => {
      onCodeNavTokenSelected({selectedText: symbol.text, lineNumber: symbol.line + 1, offset: symbol.startColumn})
    },
    [onCodeNavTokenSelected],
  )
  const copy = useCallback(async (text: string, message: string) => {
    try {
      await copyText(text)
      announce(message)
    } catch (error) {
      reportError(error)
      announce("Clipboard access failed. Select the code and use your browser's Copy command.")
    }
  }, [])
  const hash = anchorFromSelection(range, lines).replace(/^#/, '')
  const permalinkParams = params.get('plain') === '1' ? 'plain=1' : undefined
  const permalink = createPermalink({
    absolute: false,
    hash,
    params: permalinkParams,
  })
  const referencePermalink = isBrowser ? createPermalink({absolute: true, hash, params: permalinkParams}) : permalink
  const selectedLine = range.start.line + 1
  const section = sections.findLast(item => item.startLine <= selectedLine && item.endLine >= selectedLine)
  const {openUpRefSelector} = useHighlightedLineMenuOptions({
    setOpen: open => gutterSelection.onMenuOpenChange(range.start.line + 1, open),
  })
  const actions = useMemo<TaillightsGutterAction[]>(
    () => [
      {
        id: 'copy-lines',
        label: singleLine ? 'Copy line' : 'Copy lines',
        kind: 'local',
        onSelect: () =>
          void copy(
            selectedText(lines, lineRange(lines, range.start.line, lastSelectedLine(range))),
            'Copied source lines.',
          ),
      },
      {
        id: 'copy-permalink',
        label: 'Copy permalink',
        kind: 'local',
        onSelect: () => void copy(new URL(permalink, window.location.origin).href, 'Copied permalink.'),
      },
      {id: 'blame', label: 'View git blame', kind: 'link', href: getUrl({action: 'blame', hash})},
      ...(newIssuePath
        ? [
            {
              id: 'issue',
              label: 'Reference in new issue',
              kind: 'link' as const,
              href: `${newIssuePath}?permalink=${encodeURIComponent(referencePermalink)}`,
            },
          ]
        : []),
      ...(newDiscussionPath
        ? [
            {
              id: 'discussion',
              label: 'Reference in new discussion',
              kind: 'link' as const,
              href: `${newDiscussionPath}?permalink=${encodeURIComponent(referencePermalink)}`,
            },
          ]
        : []),
      ...(githubDevUrl
        ? [
            {
              id: 'github-dev',
              label: 'View file in GitHub.dev',
              kind: 'link' as const,
              href: new URL(getUrl({action: 'blob'}).replace(/^\//, ''), githubDevUrl).href,
            },
          ]
        : []),
      ...(singleLine && navigation.foldingEnabled && section
        ? [
            {
              id: 'fold',
              label: navigation.collapsed.has(section.startLine)
                ? 'Expand current section'
                : 'Collapse current section',
              kind: 'local' as const,
              onSelect: () => navigation.toggleFold(section.startLine),
            },
          ]
        : []),
      {id: 'ref', label: 'View file in different branch/tag', kind: 'local', onSelect: openUpRefSelector},
    ],
    [
      singleLine,
      copy,
      lines,
      range,
      permalink,
      getUrl,
      hash,
      newIssuePath,
      newDiscussionPath,
      referencePermalink,
      githubDevUrl,
      section,
      navigation,
      openUpRefSelector,
    ],
  )
  const actionLine = gutterSelection.owner?.lineNumber ?? (anchorRange ? lastSelectedLine(anchorRange) + 1 : null)
  const gutterActions = useMemo(
    () => ({...gutterSelection, actions, actionLine}),
    [gutterSelection, actions, actionLine],
  )
  const commands = useMemo(
    () => ({
      'code-view:open-line-menu': () => gutterSelection.openMenu(),
      'code-view:copy-permalink': () => {
        const selection = symbolsRef.current?.getSelection() ?? range
        const selectionHash = anchorFromSelection(selection, lines).replace(/^#/, '')
        void copy(createPermalink({absolute: true, hash: selectionHash}), 'Copied permalink.')
      },
    }),
    [gutterSelection, symbolsRef, range, lines, copy, createPermalink],
  )

  return (
    <HighlightedLinesProvider highlightedLines={highlightedLines}>
      {lines.some(hasHiddenUnicodeCharacters) && <HiddenUnicodeAlert />}
      <CodeBlobTaillights
        embedded
        ref={nativeRef}
        showFoldButtons={codeFoldingOption.enabled}
        codeownersErrors={codeownersErrors}
        highlightRanges={highlights}
        focusedHighlightRange={focused}
        onSymbolSelected={reportSymbol}
        gutterActions={gutterActions}
        commands={commands}
        onLineMount={onLineMount}
      />
      {copilotAccessAllowed && anchorRange && (
        <CopilotButton
          ref={copilotRef}
          rowBeginNumber={anchorRange.start.line + 1}
          rowEndNumber={lastSelectedLine(anchorRange) + 1}
          id="taillights-copilot-selection"
        />
      )}
    </HighlightedLinesProvider>
  )
}
